export interface ActivityPageSkipState {
  /** Rows of a known kind across the pages loaded so far. */
  readonly itemCount: number;
  /** Whether the most recently loaded page added no rows of a known kind. */
  readonly lastPageAddedNothing: boolean;
  readonly hasNextPage: boolean;
  readonly hasError: boolean;
  readonly isFetching: boolean;
}

export interface ActivityPageSkipPlan {
  /** Show the loading state rather than an empty feed. */
  readonly showLoading: boolean;
  /** Fetch the next page without the user asking for it. */
  readonly fetchNext: boolean;
}

/**
 * Decides how the activity list moves past pages that held only kinds this
 * build does not know. Such a page adds nothing to the list, so fetching on
 * keeps the known rows behind it reachable: at the top of the feed instead of
 * an empty state, and further down instead of a "Load more" that appears to do
 * nothing.
 *
 * Terminates because each page advances by every row the server sent (see
 * `nextActivityOffset`), so `hasNextPage` eventually turns false. Stops on an
 * error rather than retrying in a loop.
 */
export function planActivityPageSkip(state: ActivityPageSkipState): ActivityPageSkipPlan {
  const canContinue = state.hasNextPage && !state.hasError;
  const nothingShown = state.itemCount === 0;
  return {
    showLoading: nothingShown && canContinue,
    fetchNext: canContinue && !state.isFetching && (nothingShown || state.lastPageAddedNothing),
  };
}
